/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/

/** @jsx jsx */
import { jsx, css } from "@emotion/core"
import {
  FC,
  useState,
  Fragment,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react"
import {
  Drawer,
  Button,
  Input,
  Row,
  Col,
  Divider,
  Tooltip,
  Spin,
  Flex,
} from "@duxton-web/ui"
import { MoreInfoFilled, VerticalDragUniversal } from "@duxton-web/icons"
import { Form } from "grab-ui"
import { SortEndHandler } from "react-sortable-hoc"
import { debounce, isEqual, isNull, replace } from "lodash"
import { shallowEqual, useDispatch, useSelector } from "react-redux"
import { actionCreator } from "store/store"
import { AppState } from "store/store.type"
import {
  AdvancedPricing,
  GrabCategory,
  Item,
  Purchasability,
  SellingTime,
  Department,
  TranslationDetail,
  TranslationSetting,
  BusinessTypeCode,
  MenuTranslationRequestData,
  ModifierGroup,
} from "types/models/menu"
import {
  stringValidations,
  hasFormErrors,
  getValidationProps,
  newDeferredValidator,
  hasFormFieldsErrors,
  DeferredValidator,
  formErrorsDetail,
} from "utils/form_helper"
import {
  useConfirmDeleteItemOptions,
  isSpecialItem,
  generateModifierGroupRule,
} from "utils/item_helper"
import { getUCMConfig } from "utils/config"
import { useTranslation, useLocaleContext } from "common/utils/i18n"
import { minorUnitToCurrency } from "utils/currency"
import { TroyWebTracker } from "utils/tracking"
import {
  EventAction,
  EventActionEng,
  EventCategory,
  EventCategoryEng,
} from "constants/tracking"
import { MENU_DETAIL } from "constants/routes"
import { ConfirmModal } from "components/common/ConfirmModal"
import {
  TroyIcon,
  OverflowTip,
  CustomSelect,
  SortableList,
  SortableItem,
} from "components/common"
import InventoryBlockAlert from "components/InventoryBlockAlert"
import useInventoryContext from "hooks/useInventoryContext"
import {
  ItemPreviewDrawer,
  ItemDetailPhoto,
  PhotoUploadDrawer,
  AdditionalPriceDrawer,
  TranslationDrawer,
} from "components/drawers"
import PriceInputField from "components/common/forms/PriceInputField"
import { SellingTimeDropdown } from "components/dropdowns"
import { useTranslationsMapTyped } from "hooks/useTranslationsMapTyped"
import { ItemAttributeValue } from "types/models/item_attribute"
import {
  AdditionalPrice,
  additionalPriceToPriceRange,
  additionalPriceToItemAdvancedPricing,
  advancedPricingToAdditionalPrice,
} from "constants/service_based_menu"
import useFEConfig from "hooks/useFEConfig"
import {
  FeatureFlagBitPosition,
  isFeatureFlagEnabled,
  isItemAttributeStructure,
} from "utils/menu_store_helper"
import { getSellingTimeByID } from "utils/selling_time_helper"
import { getCategoryByID, isMenuSupportedLanguages } from "utils/menu_helper"
import useMenuContext from "hooks/useMenuContext"
import { useStockManagementAutoScrollContext } from "pages/menu/detail/StockManagementAutoScrollContext"

import { storageHelper } from "utils/storage"
import { getGrabXValueFromLocalStorage, MerchantPortalFeatureFlag } from "grabx"
import { getCurrencyCodeByCountryCode } from "common/utils/locale"
import { getUserCountry } from "troy-common/utils"
import { TranslationType } from "constants/menu"
import { useIsAllowSaleAlcohol } from "pages/menu/detail/hooks/useIsAllowSaleAlcohol"
import {
  generateTranslationParam,
  getItemTranslationErrorKey,
  isAutoTranslationQueueExists,
  translationMandatoryUnfill,
} from "utils/translation_helper"
import { DEFAULT_DEBOUNCE_WAIT_TIME } from "constants/ui"
import { useIsAllowSaleTobacco } from "pages/menu/detail/hooks/useIsAllowSaleTobacco"
import { AxiosAction } from "types/api"
import { GetAlcoholPredictionResponse } from "types/api/menuGetAlcoholPrediction.type"
import { useGetModifierGroupsByIDsQuery } from "queries"
import { useEnablePartialMenu } from "hooks/useEnablePartialMenu"
import { useSellingTimes } from "hooks/useSellingTimes"
import { useMenuEditorStatus } from "pages/menu/detail/hooks/useMenuEditorStatus"
import { EditSpecialItem, ItemExceedChangeLimit } from "../alerts"
import {
  drawerContentStyle,
  listModifierWrapperStyles,
  drawerFooter,
  deleteItemBtn,
  itemDrawerBodyStyle,
  drawerFooterSpacing,
  itemDrawerInfoIconStyle,
} from "../styles"
import { ItemSizeFieldsDrawerSection } from "./ItemSizeFieldsDrawerSection"
import { ItemDrawerType } from "./constant"
import { ServiceHourList } from "../../list/ServiceHourList"
import useEditableFields from "./useEditableFields"
import BrandSelectField from "./BrandSelectField"
import ItemPurchasabilityCheckboxGroup, {
  ItemPurchasabilityCheckboxGroupProps,
} from "./ItemPurchasabilityCheckboxGroup"
import ItemMultiPics from "./ItemMultiPics"
import StockManagement from "./StockManagement"
import styles from "./styles.modules.css"
import { AttributeDrawer } from "../AttributeDrawer"
import { LinkedModifierGroupsLoading } from "./LinkedModifierGroupsLoading"

export interface OnSaveParams {
  form: WrappedFormUtils
  newPhotoUploaded: boolean // legacy - use hasItemChanged instead if you're trying to determine whether or not the item changed
  priceValidator: DeferredValidator
  uploadedImg: string
  uploadedImages: string[]
  hasItemChanged: boolean
  otherItemFields: {
    advancedPricing?: AdvancedPricing
    purchasability?: Purchasability
    prediction?: GetAlcoholPredictionResponse["predictions"]
  }
  attributes?: ItemAttributeValue[]
  selectedSellingTime?: SellingTime
  callSupportedClusterEndpoint?: boolean
  newAttributes?: ItemAttributeValue[]
  translation?: TranslationDetail
  isItemValidation?: boolean // To mark that we want to validate the item only
  successCallback?: () => Promise<void>
}

export interface ItemDrawerProps extends FormComponentProps {
  visible: boolean

  onClose(e: any): void

  type?: ItemDrawerType
  onSortModifierGroup?: SortEndHandler
  onSave?: (params: OnSaveParams) => void
  onDelete?: () => Promise<void>
  isItemEditDisabled?: boolean
  onSaveStockManagement?: (
    form: WrappedFormUtils,
    showSuccess?: boolean,
  ) => void
}

const weightKey = "weight"

export const ItemDrawerComponent: FC<ItemDrawerProps> = ({
  visible,
  onClose,
  type = "menu",
  onSortModifierGroup,
  onSave,
  onDelete,
  form,
  isItemEditDisabled = false,
  onSaveStockManagement,
}: ItemDrawerProps) => {
  const mustEditThroughMenu = type === "inventory"
  const dispatch = useDispatch()
  const { isInventory } = useInventoryContext()
  const { isServiceBasedMenuEnabled } = useMenuContext()

  const stockManagementAnchorRef = useRef<HTMLDivElement>(null)
  const stockManagementFormRef = useRef<FormComponentProps>(null)

  const enablePartialMenu = useEnablePartialMenu()
  const { isEditAllowed } = useMenuEditorStatus()

  const {
    merchantID,
    outlet,
    outletV2,
    menu: { sections, categories, attributesData },
    drawer: {
      item,
      sourceShowItem,
      linkedModifierGroupsDrawerState,
      itemSubDepartments,
      createdSellingTimeID,
      showUploadPhoto = false,
    },
    departments,
    subDepartments,
    isMartStore,
    isMenuGroup,
    isMartDepartmentStructure,
    isSellingTimeStructure,
    merchantGroupID,
    menuGroupDetail: { menuGroupID, businessType, languageSettings },
    selectedGrabCategory,
    suggestedDepartments,
    predictionIDGrabCategory,
    isDepartmentTaggingMenuGroupEnabled,
    isMoreDepartments,
    isFetchingDepartments,
    isEnableAttributes,
    alcoholPredictionLoading,
    enableMenuTranslationPortal,
    enableTroyNewMerchantEndpoint,
    enableAlcoholSetting,
    tobaccoPredictionLoading,
    autoTranslationQueue,
    originalTranslationFromDSMap,
  } = useSelector((state: AppState) => state?.menu, shallowEqual)

  const { sellingTimes } = useSellingTimes()

  const { editableFields, isAllFieldsEditable, multiPics } = useEditableFields()

  const isAllowSaleAlcohol = useIsAllowSaleAlcohol()
  const isAllowSaleTobacco = useIsAllowSaleTobacco()

  // New department list if there is no selected department in default department list
  const [newDepartmentList, setNewDepartmentList] = useState<Department[]>(
    departments,
  )
  const [departmentPage, setDepartmentPage] = useState(1)

  const enableStockManagement = enableTroyNewMerchantEndpoint
    ? !!outletV2?.merchantFlag?.isInventoryManagementEnabled && isInventory
    : !!outlet?.isInventoryManagementEnabled && isInventory

  const outletCurrency =
    (enableTroyNewMerchantEndpoint
      ? outletV2?.currency?.code
      : outlet?.currency) || getCurrencyCodeByCountryCode(getUserCountry())
  const outletTimezone = enableTroyNewMerchantEndpoint
    ? outletV2?.timezone
    : outlet?.timezone
  const outletName = enableTroyNewMerchantEndpoint
    ? outletV2?.name
    : outlet?.merchantName

  const {
    shouldAutoScrollToStockManagement,
    setShouldAutoScrollToStockManagement,
  } = useStockManagementAutoScrollContext()
  useEffect(() => {
    if (visible && enableStockManagement && shouldAutoScrollToStockManagement) {
      setTimeout(() => {
        stockManagementAnchorRef.current?.scrollIntoView({ behavior: "smooth" })
        setShouldAutoScrollToStockManagement(false)
      }, 1000)
    }
  }, [
    visible,
    shouldAutoScrollToStockManagement,
    setShouldAutoScrollToStockManagement,
    enableStockManagement,
  ])

  const troyEventCategory =
    sourceShowItem === EventCategory.menuOverviewModifyItem
      ? EventCategory.menuOverviewModifyItem
      : EventCategory.menuItemsModifyitem

  const {
    linkedModifierGroups,
    hasChanged: hasLinkedModifierGroupsChanged,
    hasBeenRearranged: hasLinkedModifierGroupsRearranged,
  } = linkedModifierGroupsDrawerState ?? {}

  // Use RTK Query for linked modifier groups when partial menu is enabled
  const linkedModifierGroupIDs = item?.linkedModifierGroupIDs ?? []
  const {
    data: modifierGroupsByIDsData,
    isLoading: isLoadingLinkedModifierGroups,
  } = useGetModifierGroupsByIDsQuery(
    { merchantID, merchantGroupID, modifierGroupIDs: linkedModifierGroupIDs },
    { skip: !enablePartialMenu || !linkedModifierGroupIDs.length },
  )

  // Map linkedModifierGroupIDs to modifier groups from RTK Query response (partial menu only)
  const linkedModifierGroupsFromRTK = useMemo(() => {
    if (!enablePartialMenu || !modifierGroupsByIDsData?.modifierGroups) {
      return undefined
    }
    const modifierGroupsMap = new Map(
      modifierGroupsByIDsData.modifierGroups.map(mg => [
        mg.modifierGroupID,
        mg,
      ]),
    )
    // Map by linkedModifierGroupIDs order, filter out any not found
    const result = linkedModifierGroupIDs
      .map(id => modifierGroupsMap.get(id))
      .filter(Boolean)
    // Cast to ModifierGroup[] (same pattern as LinkModifierGroupDrawer)
    return (result as unknown) as ModifierGroup[]
  }, [enablePartialMenu, modifierGroupsByIDsData, linkedModifierGroupIDs])

  const isEdit = !!item?.itemID

  const trackingLabels: any = {
    ...(merchantID
      ? { STORE_ID: merchantID }
      : { MERCHANT_GROUP_ID: merchantGroupID, MENU_GROUP_ID: menuGroupID }),
    ITEM_ID: item?.itemID ?? "",
    INTENT: isEdit ? "EDIT_ITEM" : "ADD_ITEM",
    IS_PARTIAL_MENU: enablePartialMenu,
  }

  const [uploadedImg, setUploadedImg] = useState("")

  const [itemBrand, setItemBrand] = useState<ItemAttributeValue>()

  const isSpecial = item ? isSpecialItem(item) : false

  const isAllowEditItem = isEditAllowed && !isItemEditDisabled

  const isAllowDelete = isEditAllowed && (!isSpecial || enableAlcoholSetting)

  const mexItemAttributes = enableTroyNewMerchantEndpoint
    ? getGrabXValueFromLocalStorage<boolean>(
        MerchantPortalFeatureFlag.mexItemAttributes,
      )
    : isItemAttributeStructure(outlet)

  const brandListingGrabX = enableTroyNewMerchantEndpoint
    ? getGrabXValueFromLocalStorage<boolean>(
        MerchantPortalFeatureFlag.brandListing,
      )
    : isFeatureFlagEnabled(FeatureFlagBitPosition.brandListing, outlet)

  const isAttributeAvailable = isMenuGroup
    ? isEnableAttributes && isAllowEditItem
    : mexItemAttributes && isAllowEditItem

  const {
    setFields,
    setFieldsValue,
    getFieldDecorator,
    getFieldValue,
    getFieldsValue,
    isFieldsTouched,
    validateFields,
  } = form

  // showUploadPhoto - is used to directly go to photo upload screen
  const [photoDrawerVisible, setPhotoDrawerVisible] = useState(showUploadPhoto)

  const newPhotoUploaded = uploadedImg !== (item?.imageURL ?? "")

  const [showPreview, setShowPreview] = useState<boolean>(false)

  const [uploadPhotoError, setUploadPhotoError] = useState<string>()
  const [uploadPhotoWarning, setUploadPhotoWarning] = useState<string>()

  const [previewItem, setPreviewItem] = useState<Item>()

  const [photoUploadTitle, setPhotoUploadTitle] = useState<string>("")

  const [showConfirmDelete, setShowConfirmDelete] = useState(false)

  const [showAttributeDrawer, setShowAttributeDrawer] = useState(false)

  const [selectedSellingTime, setSelectedSellingTime] = useState<
    SellingTime | undefined
  >()

  const [disabledPurchasabilities, setDisabledPurchasabilities] = useState<
    string[]
  >()

  const [showAdditionalPriceDrawer, setShowAdditionalPriceDrawer] = useState(
    false,
  )

  const [showTranslationDrawer, setShowTranslationDrawer] = useState(false)

  const nameTranslation = item?.nameTranslation
  const descriptionTranslation = item?.descriptionTranslation
  const [nameTranslationValue, setNameTranslationValue] = useState<
    TranslationSetting | undefined
  >(nameTranslation)
  const [
    descriptionTranslationValue,
    setDescriptionTranslationValue,
  ] = useState<TranslationSetting | undefined>(descriptionTranslation)

  const [isBrandNameFieldShow, setIsBrandNameFieldShow] = useState(false)

  const itemClassIDField = getFieldValue("itemClassID")

  const openAdditionalPriceDrawer = () => {
    setShowAdditionalPriceDrawer(true)
  }

  const openTranslationDrawer = () => {
    TroyWebTracker.sendEvent({
      category: EventCategoryEng.showTranslationDrawer,
      action: EventActionEng.showItemTranslation,
    })
    setShowTranslationDrawer(true)
  }

  const priceValue = isEdit
    ? minorUnitToCurrency(item?.priceInMin, outletCurrency)
    : null

  const [additionalPrice, setAdditionalPrice] = useState<AdditionalPrice>({
    regular:
      priceValue !== null && priceValue !== undefined
        ? String(priceValue)
        : null,
  })

  const feConfig = useFEConfig()

  // needed because Antd stupidly turns off the touched state if we use setFieldsValue - done by handleChangePriceInMin
  const [isTouched, setIsTouched] = useState(false)
  useEffect(() => {
    if (visible) {
      // reset touched
      setIsTouched(false)
    }
  }, [visible])

  useEffect(() => {
    if (visible && isSellingTimeStructure) {
      if (isEdit) {
        // if edit
        setSelectedSellingTime(
          getSellingTimeByID(item?.sellingTimeID, sellingTimes),
        )
      } else {
        // if create
        const category = getCategoryByID(item?.categoryID, categories)
        setSelectedSellingTime(
          getSellingTimeByID(category?.sellingTimeID, sellingTimes),
        )
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSellingTimeStructure, visible, sellingTimes])

  // Only show error after a field is touched.
  const validationProps = getValidationProps(
    form,
    ["itemName", "categoryID", "numOfPiecesPerItem", "skuID", "itemCode"],
    isAllowEditItem && isEdit,
  )

  // For partial menu, use item's own hierarchy data directly
  // For legacy menu, look up from subDepartments array
  const itemSubDepartmentID = enablePartialMenu
    ? item?.subDepartmentID
    : subDepartments.find(sd => sd.itemClassID === item?.itemClassID)
        ?.itemClassID

  const itemDepartmentID = enablePartialMenu
    ? item?.departmentID
    : subDepartments.find(sd => sd.itemClassID === item?.itemClassID)
        ?.departmentID

  const onSelectDepartment = async (departmentID: string) => {
    if (!departmentID) return

    // prevent redundant sub-department fetch
    if (!itemSubDepartments || !itemSubDepartments[departmentID]) {
      dispatch(
        actionCreator.menu.fetchSubDepartments({
          merchantID,
          departmentID,
          merchantGroupID,
          menuGroupID,
        }),
      )
    }

    if (itemDepartmentID !== departmentID) {
      setFieldsValue({
        itemClassID: undefined,
      })
      validateFields(["itemClassID"])
    }
  }

  const handleDepartmentPagination = (val: number) => {
    setDepartmentPage(val)

    dispatch(
      actionCreator.menu.fetchDepartments({
        merchantID,
        menuGroupID,
        merchantGroupID,
        page: val,
      }),
    )
  }

  const handleOnSearchDepartment = (val: string) => {
    if (val.length > 2 || val.length === 0) {
      dispatch(
        actionCreator.menu.fetchDepartments({
          searchName: val,
          menuGroupID,
          merchantGroupID,
          merchantID,
        }),
      )
    }
  }

  const [uploadedImages, setUploadedImages] = useState<string[]>([])

  const [multiPicEditingImg, setMultiPicEditingImg] = useState("")
  const [multiPicEditingImgIndex, setMultiPicEditingImgIndex] = useState(-1)

  const isDepartmentTagging = enableTroyNewMerchantEndpoint
    ? outletV2?.merchantFlag?.isDepartmentTaggingEnhancement
    : isFeatureFlagEnabled(FeatureFlagBitPosition.departmentTagging, outlet)
  // enable if store is SHOP and flag enable or menuGroup and businessType is SHOP
  const isDepartmentTaggingEnabled =
    (isMartStore && isDepartmentTagging) ||
    (businessType === BusinessTypeCode.Mart &&
      isDepartmentTaggingMenuGroupEnabled)

  const shouldPostFeedbackGrabCategory =
    isDepartmentTaggingEnabled &&
    isEdit &&
    !!suggestedDepartments?.length &&
    !!selectedGrabCategory?.itemClassID &&
    !!selectedGrabCategory?.parentClassID

  // to check if store or menuGroup businessType is SHOP
  const isMart = isMartStore || businessType === BusinessTypeCode.Mart

  useEffect(() => {
    setUploadedImg(item?.imageURL ?? "")
    setUploadedImages(item?.imageURLs?.slice(0, multiPics?.maxSize ?? 4) ?? [])
    onSelectDepartment(itemDepartmentID ?? "")
    setAdditionalPrice({
      regular:
        priceValue !== null && priceValue !== undefined
          ? String(priceValue)
          : null,
      ...advancedPricingToAdditionalPrice(
        item?.advancedPricing,
        outletCurrency,
      ),
    })
    setItemBrand(
      item?.attributes?.find(e => e.cluster?.toLowerCase() === "brand"),
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?.imageURL, item?.imageURLs, item?.attributes, item?.advancedPricing])

  const handleMount = () => {
    // validate fields on first drawer open
    form.validateFields(() => {})
  }

  const handleChangeSelectedSellingTime = (
    newSelectedSellingTime?: SellingTime,
  ) => {
    setSelectedSellingTime(newSelectedSellingTime)
    if (
      !isTouched &&
      isSellingTimeStructure &&
      item?.sellingTimeID !== newSelectedSellingTime?.sellingTimeID
    ) {
      setIsTouched(true)
    }
  }

  // set selected selling time as the new created selling time if created while this drawer still opened
  useEffect(() => {
    if (createdSellingTimeID) {
      handleChangeSelectedSellingTime(
        getSellingTimeByID(createdSellingTimeID, sellingTimes),
      )
      // clear the created selling time
      dispatch(
        actionCreator.menu.toggleDrawer({
          createdSellingTimeID: null,
        }),
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sellingTimes])

  useEffect(() => {
    if (visible) {
      handleMount()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible])

  // Set hide/show brandName field based on attribute's subDepartment
  useEffect(() => {
    if (itemClassIDField) {
      const selectedSubDepartment = subDepartments?.find(
        subDept => subDept.itemClassID === itemClassIDField,
      )
      const itemAttributeList = selectedSubDepartment?.attributeCluster ?? []

      if (
        itemAttributeList.length &&
        (itemAttributeList.includes("brand") ||
          itemAttributeList.includes("Brand"))
      ) {
        setIsBrandNameFieldShow(true)
      } else {
        setIsBrandNameFieldShow(false)
      }
    }
  }, [itemClassIDField, subDepartments])

  const textMap = useTranslationsMapTyped({
    createSuccess: "menu.notifications.createItem.successMessage",
    editSuccess: "menu.notifications.editItem.successMessage",
    createError: "menu.notifications.createItem.errorMessage",
    deleteSuccess: "menu.notifications.deleteItem.successMessage",
    modifierGroups: "menu.drawers.item.modifierGroups",
    deleteItem: "menu.drawers.item.buttons.deleteItem",
    addPhoto: "menu.item.modal.photo.addPhoto",
    errorRequired: "menu.errors.requiredField",
    errorDecimals: "menu.errors.number.decimals",
    errorPriceExceedLimit: "menu.errors.price.priceExceedLimit",
    errorPriceInvalid: "menu.errors.price.priceInvalid",
    errorExceedMaxItemLimit: "menu.errors.item.exceedMaxItemLimit",
    changeDailyLimit: "menu.errors.changeDailyLimit.title",
    errorCharacterLimit: "menu.errors.characterLimit",
    errorItemPromo: "menu.errors.item.itemPromo",
    errorPriceExceedConstraint: "menu.errors.price.priceExceedConstraint",
    modifierGroupPickUpTo: "menu.item.modifierGroup.pickUpTo",
    pickExactly: "menu.item.modifierGroup.pick",
    pickXtoY: "menu.item.modifierGroup.pickXtoY",
    noDataAvailable: "menu.item.modifierGroup.noDataAvailable",
    errorArchived: "menu.errors.item.archived",
    editPhoto: "menu.item.modal.title.editPhoto",
    labelCategory: "menu.items.headings.category",
    labelSubCategory: "catalogue.item.labels.subCategory",
    labelPurchasableVia: "menu.drawers.item.labels.labelPurchasableVia",
    edit: "menu.actions.edit",
    linkItemLimit: "menu.errors.linkItemLimit",
    itemNamePlaceholder: "menu.drawers.item.placeholders.name",
    itemCode: "catalogue.item.labels.itemCode",
    itemCodePlaceholder: "catalogue.item.placeholders.itemCode",
    skuID: "catalogue.drawers.barcode.label",
    skuIDPlaceholder: "catalogue.drawers.barcode.placeholder",
    skuIDTooltip: "catalogue.drawers.barcode.tooltip",
    errorBarcodeExceedDigits: "catalogue.errors.barcodeExceedDigits",
    errorBarcodeNumeric: "catalogue.errors.barcodeNumeric",
    linkOptGroupHint: "menu.drawers.item.linkOptionGroupHint",
    linkOptGroupDepartmentHint:
      "menu.drawers.item.linkOptionGroupDepartmentHint",
    linkOptGroupBtn: "menu.drawers.item.buttons.linkModifierGroups",
    uploadFailed: "menu.errors.uploadFailed",
    errorMinMaxNoOfItems: "catalogue.errors.noOfItemsMinMax",
    errorMinMaxSizePerUnit: "catalogue.errors.sizePerUnitMinMax",
    addItem: "menu.drawers.item.buttons.addItem",
    saveChanges: "menu.buttons.saveChanges",
    saveChangesEverywhereTitle:
      "menu.drawers.item.menuGroup.saveChanges.everywhere.title",
    saveChangesEverywhereDescription:
      "menu.drawers.item.menuGroup.saveChanges.everywhere.description",
    saveChangesSelectedTitle:
      "menu.drawers.item.menuGroup.saveChanges.selected.title",
    saveChangesSelectedDescription:
      "menu.drawers.item.menuGroup.saveChanges.selected.description",
    additionalPriceLink: "menu.drawers.item.changePriceByOrderType",
    sellingTimeLabel: "menu.sellingTime.label.sellingTime",
    done: "menu.buttons.done",
    grabCategoryLabel: "dept.tagging.field.label",
    grabCategoryPlaceholder: "dept.tagging.field.placeholder",
    grabCategoryTooltip: "dept.tagging.field.tooltip",
    menuDrawerItemNameInDefaultLanguage:
      "menu.drawers.item.labels.nameInDefaultLanguage",
    menuDrawerItemDescriptionInDefaultLanguage:
      "menu.drawers.item.labels.descriptionInDefaultLanguage",
    nameLabel: "menu.drawers.item.labels.name",
    descriptionLabel: "menu.drawers.item.labels.description",
    translateButtonText: "menu.drawers.item.buttons.translate",
    itemAttributeButtonCategory: "itemAttributes.button.category",
    menuTranslationTitle: "menuTranslation.title",
    menuTranslationEditTranslation: "menuTranslation.editTranslation",
    menuTranslationItemLabel: "menuTranslation.itemLabel",
    menuTranslationErrorAutoTranslation: "menuTranslation.errorAutoTranslation",
    menuTranslationDetailItem: "menuTranslation.detail.item",
    menuTranslationLoadingAutoTranslation:
      "menuTranslation.loadingAutoTranslation",
  })

  const isAutoTranslationLoading = isAutoTranslationQueueExists({
    queue: autoTranslationQueue,
    entityType: TranslationType.item,
  })

  const getSaveButtonText = (defaultText: string) => {
    if (isAutoTranslationLoading) {
      return textMap.menuTranslationLoadingAutoTranslation
    }
    return defaultText
  }

  const handleSetUploadedPhoto = (image: string) => {
    setUploadedImg(image)
    if (!image) {
      setUploadPhotoError(undefined)
      setUploadPhotoWarning(undefined)
    }
  }

  const handleSubmitUploadPhoto = (photoUrl: string) => {
    setUploadedImg(photoUrl)
  }

  const handleTogglePhotoUploaderVisible = (val: boolean) => {
    setPhotoDrawerVisible(val)
    dispatch(
      actionCreator.menu.toggleDrawer({
        showUploadPhoto: val,
      }),
    )
  }

  const priceValidator = newDeferredValidator(
    form,
    [
      {
        pattern: stringValidations.numericalAmount,
        message: textMap.errorPriceInvalid,
      },
    ],
    [
      {
        pattern: stringValidations.required,
        message: textMap.errorRequired,
      },
      {
        pattern: stringValidations.moreThanZeroDecimal,
        message: textMap.errorPriceInvalid,
      },
    ],
  )

  const handleAddAttribute = () => {
    setShowAttributeDrawer(true)
  }

  const removeErrors = (target: string[]) => {
    target.map(field =>
      setFields({
        [field]: {
          errors: undefined,
        },
      }),
    )
  }

  const handleBackAttribute = () => {
    removeErrors(formErrorsDetail(form.getFieldsError()))
    setShowAttributeDrawer(false)
  }

  const skipStepAndSavePhoto = () => {
    handleSaveItemDrawer(item?.attributes)
  }

  const handleGetItemPrediction = async () => {
    if (isAllowSaleAlcohol || isAllowSaleTobacco) {
      const data = getFieldsValue()
      const currentName = data.itemName
      const currentDesc = data.description
      const currentPhotos = multiPics?.enable ? uploadedImages : [uploadedImg]

      // Compare with initial values
      const isNameChanged = currentName !== (item?.itemName ?? "")
      const isDescChanged = currentDesc !== (item?.description ?? "")
      const isPhotosChanged = multiPics?.enable
        ? !isEqual(
            currentPhotos,
            item?.imageURLs?.slice(0, multiPics?.maxSize ?? 4) ?? [],
          )
        : currentPhotos[0] !== (item?.imageURL ?? "")

      // Only call API if any relevant field has changed
      if (!isNameChanged && !isDescChanged && !isPhotosChanged) {
        return undefined
      }

      const body = {
        name: currentName,
        desc: currentDesc,
        photos: currentPhotos,
      }

      const res: AxiosAction<GetAlcoholPredictionResponse> = await dispatch(
        actionCreator.menu.getItemPrediction({
          data: body,
          merchantID,
          merchantGroupID,
          isMenuGroup,
          menuGroupID,
        }) as any,
      )

      if (!res.error) {
        return res.payload?.data?.predictions
      }
    }

    return undefined
  }

  const handleSaveWithSkip = () => {
    form.validateFields(["itemClassID"], async formError => {
      if (formError) return
      if (showUploadPhoto) {
        skipStepAndSavePhoto()
      } else {
        handleSaveItemDrawer(undefined, true)
      }
    })
  }

  const handleSaveItemDrawer = async (
    selectedAttributes?: ItemAttributeValue[],
    isItemValidation?: boolean,
  ) => {
    const newAdvancedPricing = additionalPriceToItemAdvancedPricing(
      additionalPrice,
      outletCurrency,
    )
    const hasAdvancedPricingChanged =
      !!feConfig.data && !isEqual(newAdvancedPricing, item?.advancedPricing)
    const newPurchasability = disabledPurchasabilities?.reduce<Purchasability>(
      (acc, curr) => ({ ...acc, [curr]: false }),
      {},
    )
    const hasPurchasabilityChanged =
      !!feConfig.data && !isEqual(newPurchasability, item?.purchasability)
    let itemAttributes: ItemAttributeValue[] = item?.attributes ?? []
    if (itemBrand && !isAttributeAvailable) {
      itemAttributes = [
        ...itemAttributes.filter(e => e.cluster?.toLowerCase() !== "brand"),
        itemBrand,
      ].map(e => ({ ...e, itemID: item?.itemID }))
    }

    if (!isEdit && isAttributeAvailable && selectedAttributes) {
      itemAttributes = itemAttributes.concat(selectedAttributes)
    }
    const translationValue = {
      nameTranslation: generateTranslationParam({
        menuTranslationSetting,
        translationSetting: nameTranslationValue,
        originalTranslationFromDSMap,
        entity: "item",
        textType: "name",
        text: getFieldValue("itemName"),
      }),
      descriptionTranslation: generateTranslationParam({
        menuTranslationSetting,
        translationSetting: descriptionTranslationValue,
        originalTranslationFromDSMap,
        entity: "item",
        textType: "description",
        text: getFieldValue("description"),
      }),
    }

    let itemPrediction: GetAlcoholPredictionResponse["predictions"] | undefined
    if (isItemValidation) {
      itemPrediction = await handleGetItemPrediction()
    }

    onSave?.({
      form,
      newPhotoUploaded,
      priceValidator,
      uploadedImg: multiPics?.enable ? "" : uploadedImg,
      uploadedImages,
      hasItemChanged:
        form.isFieldsTouched() ||
        newPhotoUploaded ||
        hasAdvancedPricingChanged ||
        hasPurchasabilityChanged ||
        isTouched,
      otherItemFields: {
        ...(feConfig.data // these are the fields that depend on feConfig - if they're not loaded just assume that they haven't changed
          ? {
              advancedPricing: newAdvancedPricing,
              purchasability: newPurchasability,
            }
          : {}),
        prediction: itemPrediction,
      },
      attributes: itemAttributes,
      selectedSellingTime,
      newAttributes: isEdit ? selectedAttributes : undefined,
      translation: translationValue,
      isItemValidation,
      successCallback: isItemValidation
        ? async () => handleAddAttribute()
        : undefined,
    })

    // Stop here if it is only the validation step
    if (isItemValidation) {
      return
    }

    if (stockManagementFormRef.current) {
      onSaveStockManagement?.(stockManagementFormRef.current.form)
    }

    // Send prediction feedback for Grab Category
    if (shouldPostFeedbackGrabCategory) {
      dispatch(
        actionCreator.menu.postFeedbackGrabCategory(
          {
            predictionID: predictionIDGrabCategory as string,
            suggestedDepartments,
            selectedDepartmentID: selectedGrabCategory?.parentClassID as string,
            selectedSubDepartmentID: selectedGrabCategory?.itemClassID as string,
            itemID: item?.itemID as string,
          },
          merchantID,
          merchantGroupID,
          menuGroupID,
        ),
      )
    }
  }

  const handleSave = () => {
    const newAdvancedPricing = additionalPriceToItemAdvancedPricing(
      additionalPrice,
      outletCurrency,
    )
    const hasAdvancedPricingChanged =
      !!feConfig.data && !isEqual(newAdvancedPricing, item?.advancedPricing)
    const newPurchasability = disabledPurchasabilities?.reduce<Purchasability>(
      (acc, curr) => ({ ...acc, [curr]: false }),
      {},
    )
    const hasPurchasabilityChanged =
      !!feConfig.data && !isEqual(newPurchasability, item?.purchasability)
    let itemAttributes: ItemAttributeValue[] = item?.attributes ?? []
    if (itemBrand && !isAttributeAvailable) {
      itemAttributes = [
        ...itemAttributes.filter(e => e.cluster?.toLowerCase() !== "brand"),
        itemBrand,
      ].map(e => ({ ...e, itemID: item?.itemID }))
    }
    onSave?.({
      form,
      newPhotoUploaded,
      priceValidator,
      uploadedImg: multiPics?.enable ? "" : uploadedImg,
      uploadedImages,
      hasItemChanged:
        form.isFieldsTouched() ||
        newPhotoUploaded ||
        hasAdvancedPricingChanged ||
        hasPurchasabilityChanged ||
        isTouched,
      otherItemFields: {
        ...(feConfig.data // these are the fields that depend on feConfig - if they're not loaded just assume that they haven't changed
          ? {
              advancedPricing: newAdvancedPricing,
              purchasability: newPurchasability,
            }
          : {}),
      },
      attributes: itemAttributes,
      selectedSellingTime,
    })

    if (stockManagementFormRef.current) {
      onSaveStockManagement?.(stockManagementFormRef.current.form)
    }

    // Send prediction feedback for Grab Category
    if (shouldPostFeedbackGrabCategory) {
      dispatch(
        actionCreator.menu.postFeedbackGrabCategory(
          {
            predictionID: predictionIDGrabCategory as string,
            suggestedDepartments,
            selectedDepartmentID: selectedGrabCategory?.parentClassID as string,
            selectedSubDepartmentID: selectedGrabCategory?.itemClassID as string,
            itemID: item?.itemID as string,
          },
          merchantID,
          merchantGroupID,
          menuGroupID,
        ),
      )
    }
  }

  const confirmDeleteItemOptions = useConfirmDeleteItemOptions()

  const handleDelete = () => {
    setShowConfirmDelete(false)
    onDelete?.()
  }

  const handlePreview = () => {
    setPreviewItem({
      ...form.getFieldsValue(),
      imageURL: uploadedImg,
    })
    setShowPreview(true)
    TroyWebTracker.sendEvent({
      category: troyEventCategory,
      action: EventAction.clickPreviewItem,
      labels: trackingLabels,
    })
  }

  const handleClosePreview = () => {
    setShowPreview(false)
  }

  const handleShowPhotoUploadDrawer = (title: string) => {
    handleTogglePhotoUploaderVisible(true)
    setPhotoUploadTitle(title)

    TroyWebTracker.sendEvent({
      category: troyEventCategory,
      action:
        title === textMap.addPhoto
          ? EventAction.clickAddPhoto
          : EventAction.clickEditPhoto,
    })
  }

  const handleChangeSelectedPurchasabilities = useCallback<
    NonNullable<ItemPurchasabilityCheckboxGroupProps["onChange"]>
  >(
    (uncheckedValues: string[], { touched } = {}) => {
      if (!isEqual(uncheckedValues, disabledPurchasabilities)) {
        setIsTouched(!!touched)
        setDisabledPurchasabilities(uncheckedValues)
      }
    },
    [disabledPurchasabilities],
  )

  const handleChangePriceInMin = (newPrice: string) => {
    setAdditionalPrice({
      regular: newPrice,
    })
    setIsTouched(true)
  }

  const handleChangeAdditionalPrice = (newAdditionalPrice: AdditionalPrice) => {
    setAdditionalPrice(newAdditionalPrice)
    if (
      newAdditionalPrice.regular &&
      newAdditionalPrice.regular !== getFieldValue("priceInMin")
    ) {
      setFieldsValue({
        priceInMin: newAdditionalPrice.regular,
      })
    }
    setIsTouched(true)
  }

  // Unused function for now until we have BE support to retrieve config values
  // const handleValidatePriceChange = (
  //   _rule: any,
  //   value: any,
  //   callback: (message?: string) => void,
  // ) => {
  //   if (!priceValue) {
  //     callback()
  //     return
  //   }

  //   // TODO: get % value from BE
  //   const changePercentageLimit = 25 // 25%
  //   const diff = Math.abs(
  //     priceValue - Number(value),
  //   )
  //   const changePercentage =
  //     (diff / priceValue) * 100

  //   if (
  //     changePercentage > changePercentageLimit
  //   ) {
  //     callback(
  //       replace(
  //         textMap.errorPriceExceedLimit,
  //         "$count",
  //         `${changePercentageLimit}`,
  //       ),
  //     )
  //   } else {
  //     callback()
  //   }
  // }

  // Menu Translation
  const menuTranslationSetting = menuGroupID
    ? languageSettings
    : outletV2?.menuTranslationSetting
  const isMenuTranslation =
    (enableMenuTranslationPortal &&
      isMenuSupportedLanguages(menuTranslationSetting)) ||
    false
  const isAutoTranslationEnabled =
    isMenuTranslation && !!menuTranslationSetting?.isAutoTranslationEnabled

  const translationMissing = () => {
    if (!isMenuTranslation) {
      return false
    }

    const nameErrorKey = getItemTranslationErrorKey(
      "name",
      getFieldValue("itemName"),
    )
    const descriptionErrorKey = getItemTranslationErrorKey(
      "description",
      getFieldValue("description"),
    )
    if (
      item?.translationError &&
      (item.translationError.has(nameErrorKey) ||
        item.translationError.has(descriptionErrorKey))
    ) {
      return true
    }

    if (isAutoTranslationEnabled) {
      return false
    }

    return translationMandatoryUnfill(
      menuTranslationSetting?.translationFieldMandatoryLanguages,
      nameTranslationValue,
    )
  }

  function hasErrors() {
    return (
      hasFormErrors(form, [weightKey]) ||
      hasFormFieldsErrors((form.getFieldsError()[weightKey] as any) ?? {}) ||
      // GFMEX-8286 due to priceInMin (intentionally) not showing any error until form is submitted
      !String(getFieldValue("priceInMin")) ||
      translationMissing()
    )
  }
  const emptySellingTime = isSellingTimeStructure && !selectedSellingTime
  const isSaveDisabled =
    (!isAllowEditItem &&
      !hasLinkedModifierGroupsChanged &&
      !hasLinkedModifierGroupsRearranged) ||
    hasErrors() ||
    (!newPhotoUploaded &&
      !isFieldsTouched() &&
      !isTouched &&
      !hasLinkedModifierGroupsChanged &&
      !hasLinkedModifierGroupsRearranged) ||
    isNull(getFieldValue("priceInMin")) ||
    (isMart && !itemClassIDField) ||
    (!isMartDepartmentStructure &&
      isDepartmentTaggingEnabled &&
      !itemClassIDField) ||
    emptySellingTime

  const handleMultiPicSubmitUploadPhoto = (photoUrl: string) => {
    setMultiPicEditingImg(photoUrl)
  }

  const handleMultiPicShowPhotoUploadDrawer = (image: string) => {
    // image equals to "" when adding
    if (image === "") {
      setMultiPicEditingImgIndex(-1)
    } else {
      setMultiPicEditingImgIndex(uploadedImages.indexOf(image))
    }
    setMultiPicEditingImg(image)
    handleTogglePhotoUploaderVisible(true)
    if (image === "") {
      setPhotoUploadTitle(textMap.addPhoto)
    } else {
      setPhotoUploadTitle(textMap.editPhoto)
    }
  }

  const handleSaveStockManagement = () => {
    if (stockManagementFormRef.current) {
      onSaveStockManagement?.(stockManagementFormRef.current.form, true)
    }

    // Send prediction feedback for Grab Category
    if (shouldPostFeedbackGrabCategory) {
      dispatch(
        actionCreator.menu.postFeedbackGrabCategory(
          {
            predictionID: predictionIDGrabCategory as string,
            suggestedDepartments,
            selectedDepartmentID: selectedGrabCategory?.parentClassID as string,
            selectedSubDepartmentID: selectedGrabCategory?.itemClassID as string,
            itemID: item?.itemID as string,
          },
          merchantID,
          merchantGroupID,
          menuGroupID,
        ),
      )
    }
  }

  const handleChangeStockManagement = useCallback(() => {
    setIsTouched(true)
  }, [])

  const locale = useLocaleContext("locale")

  useEffect(() => {
    // For partial menu, use item's own hierarchy data
    // For legacy menu, use itemSubDepartments from Redux
    if (isDepartmentTaggingEnabled && itemSubDepartmentID && itemDepartmentID) {
      if (enablePartialMenu && item) {
        // V3 items already have hierarchy data
        dispatch(
          actionCreator.menu.getSelectedGrabCategory({
            itemClassID: itemSubDepartmentID,
            itemClassName: item.subDepartmentName as string,
            parentClassID: itemDepartmentID,
            parentClassName: item.departmentName as string,
          }),
        )
      } else {
        // Legacy menu - use itemSubDepartments lookup
        const selectedSubDepartment = itemSubDepartments?.[
          itemDepartmentID
        ]?.find(itemSubDept => itemSubDept.ItemClassID === itemSubDepartmentID)

        if (selectedSubDepartment) {
          dispatch(
            actionCreator.menu.getSelectedGrabCategory({
              itemClassID: selectedSubDepartment.ItemClassID as string,
              itemClassName: selectedSubDepartment.Name as string,
              parentClassID: selectedSubDepartment.ParentID as string,
              parentClassName: selectedSubDepartment.ParentClassName,
            }),
          )
        }
      }
    }

    // Initiate Grab Category value for non department structure
    if (isDepartmentTaggingEnabled && isMart && !isMartDepartmentStructure) {
      dispatch(
        actionCreator.menu.getSelectedGrabCategory({
          itemClassID: item?.itemClassID as string,
          itemClassName: item?.itemClassName as string,
          parentClassID: item?.parentItemClassID as string,
          parentClassName: item?.parentItemClassName,
        }),
      )
    }
  }, [
    itemSubDepartmentID,
    itemSubDepartments,
    itemDepartmentID,
    dispatch,
    isMart,
    businessType,
    isMartDepartmentStructure,
    item,
    isDepartmentTaggingEnabled,
    setFieldsValue,
    enablePartialMenu,
  ])

  // Enable save button for newly choosed grab category
  useEffect(() => {
    if (
      isDepartmentTaggingEnabled &&
      selectedGrabCategory?.itemClassID !== item?.itemClassID
    ) {
      setIsTouched(true)
    }
  }, [selectedGrabCategory, setFieldsValue, isDepartmentTaggingEnabled, item])

  // Force to set initial value of itemClassID because ant design not initiate it if we have required validation
  useEffect(() => {
    if (isDepartmentTaggingEnabled) {
      setFieldsValue({
        itemClassID: selectedGrabCategory?.itemClassID || undefined,
      })
    }
  }, [isDepartmentTaggingEnabled, selectedGrabCategory, setFieldsValue])

  // Empty grab category in create mode
  useEffect(() => {
    if (!isEdit) {
      dispatch(
        actionCreator.menu.getSelectedGrabCategory(
          (undefined as unknown) as GrabCategory,
        ),
      )
    }
  }, [dispatch, isEdit])

  // Check if departmentID not included in departments list
  useEffect(() => {
    if (!!departments?.length && item?.parentItemClassID) {
      const isDepartmentIDIncluded = departments.some(
        department => department.itemClassID === item.parentItemClassID,
      )

      if (!isDepartmentIDIncluded) {
        setNewDepartmentList([
          ...departments,
          {
            itemClassID: item.parentItemClassID,
            itemClassName: item.parentItemClassName as string,
            subDepartments: [],
          },
        ])
      } else {
        setNewDepartmentList(departments)
      }
    } else {
      setNewDepartmentList(departments)
    }
  }, [departments, item])

  const callSupportedClusterEndpoint =
    isDepartmentTaggingEnabled && (!isEdit || item?.itemClassID)

  const countryID = storageHelper.getUserCountryID()
  const supportedValue = item?.supportedAttributeClusterIDs
  const errorShown = isItemEditDisabled && !mustEditThroughMenu

  useEffect(() => {
    if (
      callSupportedClusterEndpoint &&
      isAttributeAvailable &&
      item?.itemClassID
    ) {
      dispatch(
        actionCreator.menu.fetchSupportedAttributeCluster(
          merchantID,
          merchantGroupID,
          isMenuGroup,
          menuGroupID,
          {
            itemClassIDs: item?.itemClassID,
            countryID,
            businessType: "shop",
            itemID: item?.itemID,
          },
        ),
      )
    }
  }, [
    callSupportedClusterEndpoint,
    countryID,
    dispatch,
    isAttributeAvailable,
    isMenuGroup,
    item,
    menuGroupID,
    merchantGroupID,
    merchantID,
  ])

  const finalAttributeClusters = attributesData?.attributeClusters
  const finalAttributeClusterKey =
    callSupportedClusterEndpoint || !supportedValue
      ? Object.keys(finalAttributeClusters || [])
      : supportedValue
  const notEmptyAttributeList =
    !!finalAttributeClusterKey?.length && !!finalAttributeClusters
  // The attribute page will be displayed, even with no attributes, if alcohol setting feature is enabled
  const showingAttribute =
    !!(notEmptyAttributeList && isAttributeAvailable) || isAllowSaleAlcohol

  useEffect(() => {
    if (
      nameTranslation &&
      nameTranslation.originalText === getFieldValue("itemName")
    ) {
      setNameTranslationValue(nameTranslation)
    }
  }, [nameTranslation, getFieldValue])

  useEffect(() => {
    if (
      descriptionTranslation &&
      descriptionTranslation.originalText === getFieldValue("description")
    ) {
      setDescriptionTranslationValue(descriptionTranslation)
    }
  }, [descriptionTranslation, getFieldValue])

  const onFocusGrabCategory = async () => {
    await dispatch(
      actionCreator.menu.fetchSuggestedDepartments({
        itemName: getFieldValue("itemName") ?? encodeURI('""'),
        locale,
        menuGroupID,
        merchantGroupID,
        merchantID,
      }),
    )

    dispatch(
      actionCreator.menu.toggleDrawer({ showDepartmentTaggingDrawer: true }),
    )
  }

  const menuTranslateGetTranslation = useCallback(
    debounce((position: "itemName" | "description") => {
      const textTargetLang =
        menuTranslationSetting?.translationModelSupportedLanguages
      const fieldValue = getFieldValue(position)
      if (
        textTargetLang?.length &&
        (position === "description" || fieldValue?.trim().length) // description can be empty
      ) {
        const data: MenuTranslationRequestData = {
          textType: position === "itemName" ? "name" : position,
          entity: "item",
          text: fieldValue,
          textSourceLang: menuTranslationSetting?.menuDefaultLanguage || "",
          textTargetLang,
        }
        dispatch(
          actionCreator.menu.getTranslation({
            data,
            merchantID,
            merchantGroupID,
            menuGroupID,
          }),
        )
        TroyWebTracker.sendEvent({
          category: EventCategoryEng.translationValueFetch,
          action: EventActionEng.itemAutoTranslationFetch,
        })
        setIsTouched(true)
      }
    }, DEFAULT_DEBOUNCE_WAIT_TIME),
    [dispatch],
  )

  const handleOnCloseTranslation = (translation?: TranslationDetail) => {
    if (translation) {
      setNameTranslationValue(translation.nameTranslation)
      setDescriptionTranslationValue(translation.descriptionTranslation)
    }
    setShowTranslationDrawer(false)
    dispatch(actionCreator.menu.removeTranslationError(TranslationType.item))
  }

  // Loading state for linked modifier groups (partial menu only)
  const showLinkedModifierGroupsLoading =
    enablePartialMenu &&
    isLoadingLinkedModifierGroups &&
    !!linkedModifierGroupIDs.length

  // Show linked modifier groups list (when not loading and has data)
  const showLinkedModifierGroupsList =
    !showLinkedModifierGroupsLoading && !!linkedModifierGroups?.length

  // Show empty state for linked modifier groups (when not loading and no data)
  const showLinkedModifierGroupsEmpty =
    !showLinkedModifierGroupsLoading && !linkedModifierGroups?.length

  // Initialize Redux drawer state with RTK Query data (for partial menu only)
  // This allows rearrange/update to work using existing Redux flow
  // Only initialize if user hasn't made changes yet (hasChanged is false)
  useEffect(() => {
    const { hasChanged } = linkedModifierGroupsDrawerState ?? {}
    if (
      enablePartialMenu &&
      linkedModifierGroupsFromRTK?.length &&
      !linkedModifierGroups?.length &&
      !hasChanged
    ) {
      dispatch(
        actionCreator.menu.initLinkedModifierGroup(linkedModifierGroupsFromRTK),
      )
    }
  }, [
    enablePartialMenu,
    linkedModifierGroupsFromRTK,
    linkedModifierGroups,
    linkedModifierGroupsDrawerState,
    dispatch,
  ])

  const emptyStateIconType = isMart ? "martCart" : "forkSpoon"
  const emptyStateHintText = isMartDepartmentStructure
    ? textMap.linkOptGroupDepartmentHint
    : textMap.linkOptGroupHint

  return (
    <Fragment>
      <Drawer
        title={useTranslation(
          // eslint-disable-next-line no-nested-ternary
          isEdit
            ? // eslint-disable-next-line no-nested-ternary
              isAllowEditItem || Object.keys(editableFields).length
              ? isInventory
                ? "inventory.itemEdit"
                : "menu.drawers.item.editItem"
              : "inventory.itemDetails"
            : "menu.drawers.item.addNewItem",
        )}
        width={450}
        open={visible}
        onClose={onClose}
        css={
          !showAttributeDrawer &&
          css`
            .dui-drawer-body {
              background: #f5f5f5;
            }
          `
        }
        destroyOnClose
      >
        <div
          className={
            !showAttributeDrawer ? styles.hide : "attribute-drawer-wrapper"
          }
        >
          {showingAttribute && (
            <AttributeDrawer
              data-testid="attribute-drawer"
              onClose={onClose}
              onBackAttribute={handleBackAttribute}
              itemID={item?.itemID ?? ""}
              itemAttributes={item?.attributes}
              form={form}
              onSaveButton={handleSaveItemDrawer}
              finalAttributeClusterKey={finalAttributeClusterKey}
              attributeClusters={finalAttributeClusters}
              enableTroyNewMerchantEndpoint={!!enableTroyNewMerchantEndpoint}
              isLoadingPagination={attributesData?.isLoading}
              errorShown={errorShown}
              priceRange={additionalPriceToPriceRange(additionalPrice)}
              uploadedImages={
                multiPics?.enable ? uploadedImages : [uploadedImg]
              }
              showAttributeDrawer={showAttributeDrawer}
              specialItemType={item?.specialItemType}
            />
          )}
        </div>
        <div
          className={showAttributeDrawer ? styles.hide : ""}
          css={itemDrawerBodyStyle}
        >
          <div
            css={css`
              flex: 1 0 auto;
            `}
          >
            {mustEditThroughMenu && (
              <div
                css={css`
                  position: sticky;
                  top: 0;
                  z-index: 9999;
                `}
              >
                <InventoryBlockAlert
                  merchantID={merchantID}
                  merchantName={outletName || "Merchant"}
                  redirectRoute={MENU_DETAIL({
                    merchantID,
                    tabKey: "menuOverview",
                    itemID: item?.itemID ?? "",
                  })}
                />
              </div>
            )}
            <div css={drawerContentStyle}>
              {errorShown && (
                <ItemExceedChangeLimit itemID={item?.itemID ?? ""} />
              )}
              {isSpecial && !isAllFieldsEditable && <EditSpecialItem />}
              {!multiPics?.enable ? (
                <ItemDetailPhoto
                  data-testid="itemdetailphoto"
                  css={css`
                    margin-bottom: 16px;
                  `}
                  src={uploadedImg}
                  disabled={!isAllowEditItem || !editableFields.imageURL}
                  isInProgress={false}
                  onClickDelete={() => {
                    handleSetUploadedPhoto("")
                  }}
                  onClickEdit={() =>
                    handleShowPhotoUploadDrawer(textMap.editPhoto)
                  }
                  onClickAdd={() =>
                    handleShowPhotoUploadDrawer(textMap.addPhoto)
                  }
                  error={uploadPhotoError}
                  warning={uploadPhotoWarning}
                />
              ) : (
                <ItemMultiPics
                  images={uploadedImages}
                  multiPicsLimit={multiPics?.maxSize || 4}
                  handleEditElement={handleMultiPicShowPhotoUploadDrawer}
                  handleAddElement={handleMultiPicShowPhotoUploadDrawer}
                  onChange={(newVal: string[]) => {
                    setUploadedImages(newVal)
                    setIsTouched(true)
                  }}
                  multiPicEditingImgIndex={multiPicEditingImgIndex}
                  multiPicEditingImg={multiPicEditingImg}
                  disabled={!isAllowEditItem}
                />
              )}

              <Row gutter={6}>
                <Col span={isServiceBasedMenuEnabled ? 24 : 16}>
                  <Form.Item
                    label={textMap.nameLabel}
                    {...validationProps.itemName}
                  >
                    {getFieldDecorator("itemName", {
                      initialValue: item?.itemName,
                      rules: [
                        {
                          required: true,
                          message: textMap.errorRequired,
                        },
                        {
                          max: getUCMConfig("maxLengthItemName"),
                          message: replace(
                            textMap.errorCharacterLimit,
                            "$limit",
                            getUCMConfig("maxLengthItemName"),
                          ),
                        },
                      ],
                    })(
                      <Input
                        placeholder={textMap.itemNamePlaceholder}
                        onChange={() =>
                          isAutoTranslationEnabled &&
                          menuTranslateGetTranslation("itemName")
                        }
                        data-testid="input-name"
                        autoComplete="off"
                        disabled={!isAllowEditItem || !editableFields.itemName}
                      />,
                    )}
                  </Form.Item>
                </Col>
                {!isServiceBasedMenuEnabled && (
                  <Col span={8}>
                    <PriceInputField
                      disabled={!isAllowEditItem || !editableFields.priceInMin}
                      priceValidator={priceValidator}
                      form={form}
                      onChangePriceInMin={handleChangePriceInMin}
                      priceValue={priceValue}
                      outlet={outlet}
                      outletV2={outletV2}
                      data-testid="price-input-field"
                      validationType="item"
                      enableTroyNewMerchantEndpoint={
                        enableTroyNewMerchantEndpoint
                      }
                      required
                    />
                  </Col>
                )}
              </Row>
              {isMart && (
                <Fragment>
                  <Flex vertical>
                    <Form.Item
                      label={
                        <span>
                          {textMap.skuID}
                          <Tooltip
                            overlay={
                              <Flex vertical align="center">
                                {textMap.skuIDTooltip}
                                <img
                                  src={require("./barcode.svg")}
                                  width={200}
                                />
                              </Flex>
                            }
                            overlayStyle={{ maxWidth: "240px" }}
                          >
                            <MoreInfoFilled css={itemDrawerInfoIconStyle} />
                          </Tooltip>
                        </span>
                      }
                      {...validationProps.skuID}
                    >
                      {getFieldDecorator("skuID", {
                        initialValue: item?.skuID ?? "",
                        validateFirst: true,
                        rules: [
                          {
                            pattern: /^\d+$/,
                            message: textMap.errorBarcodeNumeric,
                          },
                          {
                            max: getUCMConfig("maxLengthSkuID"),
                            message: textMap.errorBarcodeExceedDigits,
                          },
                        ],
                      })(
                        <Input
                          data-testid="input-skuid"
                          disabled={!isAllowEditItem || !editableFields.skuID}
                          placeholder={textMap.skuIDPlaceholder}
                          autoComplete="off"
                        />,
                      )}
                    </Form.Item>
                  </Flex>
                  <Flex vertical>
                    <Form.Item
                      label={textMap.itemCode}
                      {...validationProps.itemCode}
                    >
                      {getFieldDecorator("itemCode", {
                        initialValue: item?.itemCode ?? "",
                        rules: [
                          {
                            max: getUCMConfig("maxLengthItemCode"),
                            message: replace(
                              textMap.errorCharacterLimit,
                              "$limit",
                              getUCMConfig("maxLengthItemCode"),
                            ),
                          },
                        ],
                      })(
                        <Input
                          data-testid="input-item-code"
                          disabled={
                            !isAllowEditItem || !editableFields.itemCode
                          }
                          placeholder={textMap.itemCodePlaceholder}
                          autoComplete="off"
                        />,
                      )}
                    </Form.Item>
                  </Flex>
                </Fragment>
              )}
              <Flex vertical>
                <Form.Item label={textMap.descriptionLabel}>
                  {getFieldDecorator("description", {
                    initialValue: item?.description,
                    rules: [
                      {
                        max: getUCMConfig(
                          isMart
                            ? "maxLengthMartItemDescription"
                            : "maxLengthItemDescription",
                        ),
                        message: replace(
                          textMap.errorCharacterLimit,
                          "$limit",
                          getUCMConfig(
                            isMart
                              ? "maxLengthMartItemDescription"
                              : "maxLengthItemDescription",
                          ),
                        ),
                      },
                    ],
                  })(
                    <Input.TextArea
                      data-testid="input-description"
                      placeholder={useTranslation(
                        "menu.drawers.item.placeholders.description",
                      )}
                      disabled={!isAllowEditItem || !editableFields.description}
                      onChange={() =>
                        isAutoTranslationEnabled &&
                        menuTranslateGetTranslation("description")
                      }
                    />,
                  )}
                </Form.Item>
              </Flex>
              {isMenuTranslation && (
                <div>
                  <Form.Item label={textMap.menuTranslationTitle} />
                  <span>
                    {textMap.menuTranslationDetailItem.replace(
                      "{x}",
                      menuTranslationSetting?.menuSupportedLanguages.length.toString() ||
                        "0",
                    )}
                  </span>
                  <br />
                  <Button
                    type="link"
                    data-testid="name-translation"
                    onClick={openTranslationDrawer}
                  >
                    {textMap.menuTranslationEditTranslation}
                  </Button>
                  {translationMissing() && (
                    <div className={styles.errorMessage}>
                      {textMap.menuTranslationErrorAutoTranslation}
                    </div>
                  )}
                </div>
              )}
              <Flex vertical>
                {!isMartDepartmentStructure && (
                  <Fragment>
                    {isDepartmentTaggingEnabled ? (
                      <Fragment>
                        <Form.Item
                          label={
                            <span>
                              {textMap.grabCategoryLabel}
                              <Tooltip
                                overlay={
                                  <div>{textMap.grabCategoryTooltip}</div>
                                }
                              >
                                <MoreInfoFilled css={itemDrawerInfoIconStyle} />
                              </Tooltip>
                            </span>
                          }
                          required={false}
                        >
                          {getFieldDecorator("itemClassID", {
                            initialValue:
                              selectedGrabCategory?.itemClassID || undefined,
                            rules: [
                              {
                                required: true,
                                message: "Grab Category is required",
                              },
                            ],
                          })(
                            <CustomSelect
                              placeholder={textMap.grabCategoryPlaceholder}
                              data-testid="input-grab-category"
                              options={[
                                {
                                  label:
                                    selectedGrabCategory?.itemClassName ?? "",
                                  value:
                                    selectedGrabCategory?.itemClassID ?? "",
                                },
                              ]}
                              onFocus={onFocusGrabCategory}
                              paginationProps={{ showPagination: false }}
                              dropdownStyle={{ visibility: "hidden" }}
                              disabled={!isAllowEditItem}
                            />,
                          )}
                        </Form.Item>
                        <Form.Item
                          label={
                            <span>
                              Store Category*
                              <Tooltip
                                overlay={
                                  <div>
                                    Sort items into these categories you created
                                    for your store.
                                  </div>
                                }
                              >
                                <MoreInfoFilled css={itemDrawerInfoIconStyle} />
                              </Tooltip>
                            </span>
                          }
                          {...validationProps.categoryID}
                          required={false}
                        >
                          {getFieldDecorator("categoryID", {
                            initialValue: item?.categoryID,
                            rules: [
                              {
                                required: true,
                                message: textMap.errorRequired,
                              },
                            ],
                          })(
                            <CustomSelect
                              placeholder={textMap.labelCategory}
                              disabled={
                                !isAllowEditItem || !editableFields.categoryID
                              }
                              data-testid="input-category"
                              optGroups={sections?.map(section => ({
                                label: (
                                  <OverflowTip
                                    css={css`
                                      font-weight: 500;
                                      font-size: 12px;
                                      line-height: 14px;
                                      letter-spacing: 0.5px;
                                      text-transform: uppercase;
                                    `}
                                  >
                                    {section.sectionName}
                                  </OverflowTip>
                                ),
                                options:
                                  section.categories?.map(category => ({
                                    label: category.categoryName || "",
                                    value: category.categoryID || "",
                                  })) || [],
                              }))}
                              options={
                                categories?.map(category => ({
                                  label: category.categoryName || "",
                                  value: category.categoryID || "",
                                })) || []
                              }
                              searchable
                              paginationProps={{ showPagination: false }}
                            />,
                          )}
                        </Form.Item>
                      </Fragment>
                    ) : (
                      <Form.Item
                        label={textMap.labelCategory}
                        {...validationProps.categoryID}
                      >
                        {getFieldDecorator("categoryID", {
                          initialValue: item?.categoryID,
                          rules: [
                            {
                              required: true,
                              message: textMap.errorRequired,
                            },
                          ],
                        })(
                          <CustomSelect
                            placeholder={textMap.labelCategory}
                            disabled={
                              !isAllowEditItem || !editableFields.categoryID
                            }
                            data-testid="input-category"
                            optGroups={sections?.map(section => ({
                              label: (
                                <OverflowTip
                                  css={css`
                                    font-weight: 500;
                                    font-size: 12px;
                                    line-height: 14px;
                                    letter-spacing: 0.5px;
                                    text-transform: uppercase;
                                  `}
                                >
                                  {section.sectionName}
                                </OverflowTip>
                              ),
                              options:
                                section.categories?.map(category => ({
                                  label: category.categoryName || "",
                                  value: category.categoryID || "",
                                })) || [],
                            }))}
                            options={
                              categories?.map(category => ({
                                label: category.categoryName || "",
                                value: category.categoryID || "",
                              })) || []
                            }
                            searchable
                            paginationProps={{ showPagination: false }}
                          />,
                        )}
                      </Form.Item>
                    )}
                  </Fragment>
                )}
                {isMartDepartmentStructure && (
                  <Fragment>
                    {isDepartmentTaggingEnabled ? (
                      <Form.Item
                        label={
                          <span>
                            {textMap.grabCategoryLabel}
                            <Tooltip
                              overlay={<div>{textMap.grabCategoryTooltip}</div>}
                            >
                              <MoreInfoFilled css={itemDrawerInfoIconStyle} />
                            </Tooltip>
                          </span>
                        }
                        required={false}
                      >
                        {getFieldDecorator("itemClassID", {
                          initialValue:
                            selectedGrabCategory?.itemClassID || undefined,
                          rules: [
                            {
                              required: true,
                              message: "Grab Category is required",
                            },
                          ],
                        })(
                          <CustomSelect
                            placeholder={textMap.grabCategoryPlaceholder}
                            data-testid="input-grab-category"
                            options={[
                              {
                                label:
                                  selectedGrabCategory?.itemClassName ?? "",
                                value: selectedGrabCategory?.itemClassID ?? "",
                              },
                            ]}
                            onFocus={onFocusGrabCategory}
                            paginationProps={{ showPagination: false }}
                            dropdownStyle={{ visibility: "hidden" }}
                            disabled={!isAllowEditItem}
                          />,
                        )}
                      </Form.Item>
                    ) : (
                      <Fragment>
                        <Form.Item
                          label={textMap.labelCategory}
                          {...validationProps.categoryID}
                        >
                          {getFieldDecorator("departmentID", {
                            initialValue: itemDepartmentID,
                            rules: [
                              {
                                required: true,
                                message: textMap.errorRequired,
                              },
                            ],
                          })(
                            <CustomSelect
                              placeholder={textMap.labelCategory}
                              disabled={
                                !isAllowEditItem ||
                                !editableFields.itemClassName ||
                                !editableFields.itemClassID
                              }
                              data-testid="input-department"
                              options={newDepartmentList?.map(department => ({
                                label: department.itemClassName,
                                value: department.itemClassID,
                              }))}
                              searchable
                              onChange={onSelectDepartment}
                              paginationProps={{
                                current: departmentPage,
                                showPagination: Boolean(
                                  newDepartmentList?.length,
                                ),
                                onChange: handleDepartmentPagination,
                                pageSize: 100,
                                total: isMoreDepartments
                                  ? departmentPage * 100 + 1
                                  : departmentPage * 100,
                                hideOnSinglePage: true,
                              }}
                              loading={isFetchingDepartments}
                              onSearch={handleOnSearchDepartment}
                            />,
                          )}
                        </Form.Item>
                        <Form.Item
                          label={textMap.labelSubCategory}
                          {...validationProps.categoryID}
                        >
                          {getFieldDecorator("itemClassID", {
                            initialValue: itemSubDepartmentID,
                            rules: [
                              {
                                required: true,
                                message: textMap.errorRequired,
                              },
                            ],
                          })(
                            <CustomSelect
                              placeholder={textMap.labelSubCategory}
                              disabled={
                                !isAllowEditItem ||
                                !getFieldValue("departmentID") ||
                                !editableFields.itemClassID ||
                                !editableFields.itemClassName
                              }
                              data-testid="input-item-class"
                              options={
                                itemSubDepartments
                                  ? itemSubDepartments[
                                      getFieldValue("departmentID")
                                    ]?.map(itemClass => ({
                                      label: itemClass.Name,
                                      value: itemClass.ItemClassID,
                                    }))
                                  : []
                              }
                              searchable
                              paginationProps={{ showPagination: false }}
                            />,
                          )}
                        </Form.Item>
                      </Fragment>
                    )}
                  </Fragment>
                )}
              </Flex>
              {brandListingGrabX &&
                !isAttributeAvailable &&
                ((isMartDepartmentStructure && isBrandNameFieldShow) ||
                  !isMartDepartmentStructure) && (
                  <Flex vertical>
                    <BrandSelectField
                      onChange={newBrand => {
                        setItemBrand(newBrand)
                        setIsTouched(true)
                      }}
                      value={itemBrand}
                      disabled={
                        !isAllowEditItem ||
                        !editableFields.attributes ||
                        (isMartDepartmentStructure && !itemClassIDField)
                      }
                    />
                  </Flex>
                )}
              {isSellingTimeStructure && (
                <Flex vertical>
                  <Form.Item required label={textMap.sellingTimeLabel}>
                    <SellingTimeDropdown
                      disabled={!isAllowEditItem}
                      selectedSellingTime={selectedSellingTime}
                      sellingTimes={sellingTimes}
                      eventTrackerObject={{
                        eventCategoryTracker: troyEventCategory,
                        itemID: item?.itemID,
                      }}
                      onChange={handleChangeSelectedSellingTime}
                    />
                  </Form.Item>
                  <ServiceHourList sellingTime={selectedSellingTime} />
                </Flex>
              )}
            </div>
            {isServiceBasedMenuEnabled && (
              <div
                css={css`
                  ${drawerContentStyle};
                  margin-top: 8px;
                `}
              >
                {!feConfig.isLoading ? (
                  feConfig.showPurchasability && (
                    <Form.Item
                      label={textMap.labelPurchasableVia}
                      css={css`
                        margin-bottom: 0;
                      `}
                    >
                      <ItemPurchasabilityCheckboxGroup
                        item={item}
                        onChange={handleChangeSelectedPurchasabilities}
                        disabled={
                          !isAllowEditItem || !editableFields.purchasability
                        }
                      />
                    </Form.Item>
                  )
                ) : (
                  <Spin />
                )}
                <PriceInputField
                  validationType="item"
                  disabled={!isAllowEditItem || !editableFields.priceInMin}
                  priceValidator={priceValidator}
                  priceValue={priceValue}
                  form={form}
                  onChangePriceInMin={handleChangePriceInMin}
                  outlet={outlet}
                  outletV2={outletV2}
                  onPriceRangeClick={openAdditionalPriceDrawer}
                  priceRange={additionalPriceToPriceRange(additionalPrice)}
                  data-testid="price-input-field"
                  enableTroyNewMerchantEndpoint={enableTroyNewMerchantEndpoint}
                  required
                />

                {!feConfig.isLoading ? (
                  feConfig.showAdditionalPriceDrawerLink && (
                    <Button
                      type="link"
                      onClick={openAdditionalPriceDrawer}
                      disabled={!isAllowEditItem || !editableFields.priceInMin}
                    >
                      {textMap.additionalPriceLink}
                    </Button>
                  )
                ) : (
                  <Spin />
                )}
              </div>
            )}
            {isMart && (
              <ItemSizeFieldsDrawerSection
                form={form}
                weightKey={weightKey}
                isAllowEdit={isAllowEditItem}
                editableFields={editableFields}
              />
            )}
            <Row>
              <Col span={24}>
                <div className="section-separator">
                  <span>{textMap.modifierGroups}</span>
                  {linkedModifierGroups &&
                    linkedModifierGroups?.length > 0 &&
                    isAllowEditItem &&
                    !isLoadingLinkedModifierGroups && (
                      <span
                        css={css`
                          float: right;
                          color: #00a5cf;
                          cursor: pointer;
                        `}
                        onClick={() =>
                          dispatch(
                            actionCreator.menu.toggleDrawer({
                              showLinkModifierGroup: true,
                            }),
                          )
                        }
                      >
                        {textMap.edit}
                      </span>
                    )}
                </div>
              </Col>
            </Row>
            <Row className="linked-modifier-wrapper">
              <Col span={24}>
                {showLinkedModifierGroupsLoading && (
                  <LinkedModifierGroupsLoading
                    count={linkedModifierGroupIDs.length}
                  />
                )}

                {showLinkedModifierGroupsList && (
                  <SortableList
                    onSortEnd={onSortModifierGroup}
                    shouldCancelStart={isAllowEditItem ? undefined : () => true}
                    lockAxis="y"
                  >
                    {linkedModifierGroups!.map((modifierGroup, i) => (
                      <SortableItem
                        key={modifierGroup.modifierGroupID}
                        index={i}
                        disabled={!isAllowEditItem}
                      >
                        <div css={listModifierWrapperStyles(isAllowEditItem)}>
                          {isAllowEditItem && (
                            <VerticalDragUniversal
                              css={css`
                                margin-right: 8px;
                              `}
                            />
                          )}
                          <OverflowTip className="modifier-name">
                            {modifierGroup?.modifierGroupName}
                          </OverflowTip>
                          <span
                            data-testid={`list-modgroup-${modifierGroup.modifierGroupID}`}
                            className="modifier-rule"
                          >
                            {generateModifierGroupRule(modifierGroup, {
                              pickExactly: textMap.pickExactly,
                              pickXtoY: textMap.pickXtoY,
                              modifierGroupPickUpTo:
                                textMap.modifierGroupPickUpTo,
                            })}
                          </span>
                        </div>
                        {i < linkedModifierGroups!.length - 1 && (
                          <div
                            css={css`
                              padding: 0 24px;
                              background-color: #fff;
                            `}
                          >
                            <Divider
                              css={css`
                                margin: 0;
                              `}
                            />
                          </div>
                        )}
                      </SortableItem>
                    ))}
                  </SortableList>
                )}
                {showLinkedModifierGroupsEmpty && (
                  <div
                    style={{
                      textAlign: "center",
                    }}
                    className="empty-mg-list"
                  >
                    <Flex justify="center">
                      <TroyIcon
                        css={css`
                          margin-top: 27px;
                          margin-bottom: 14.13px;
                        `}
                        type={emptyStateIconType}
                      />
                    </Flex>
                    <p>{emptyStateHintText}</p>
                    <Flex justify="center">
                      <Button
                        disabled={!isAllowEditItem}
                        onClick={() =>
                          dispatch(
                            actionCreator.menu.toggleDrawer({
                              showLinkModifierGroup: true,
                            }),
                          )
                        }
                      >
                        {textMap.linkOptGroupBtn}
                      </Button>
                    </Flex>
                  </div>
                )}
              </Col>
            </Row>
          </div>
          {enableStockManagement && (
            <div ref={stockManagementAnchorRef}>
              <StockManagement
                wrappedComponentRef={stockManagementFormRef}
                itemStock={item?.itemStock}
                timezone={outletTimezone}
                onChange={handleChangeStockManagement}
              />
            </div>
          )}

          {isEdit && !mustEditThroughMenu && (
            <Fragment>
              <div css={deleteItemBtn}>
                <Button
                  danger
                  block
                  onClick={() => setShowConfirmDelete(true)}
                  disabled={!isAllowDelete}
                  data-testid="button-delete"
                >
                  {textMap.deleteItem}
                </Button>
              </div>
              <ConfirmModal
                title={confirmDeleteItemOptions.title}
                content={confirmDeleteItemOptions.content}
                visible={showConfirmDelete}
                onCancel={() => setShowConfirmDelete(false)}
                onConfirm={handleDelete}
                data-testid="modal-delete"
              />
            </Fragment>
          )}
          <span css={drawerFooterSpacing} />
          <div css={drawerFooter}>
            <Row gutter={12}>
              <Col span={6}>
                <Button
                  data-testid="button-preview"
                  block
                  onClick={handlePreview}
                  disabled={hasErrors()}
                >
                  {useTranslation("menu.drawers.item.buttons.preview")}
                </Button>
              </Col>
              <Col flex="auto">
                {type === "menu" && showingAttribute ? (
                  <Button
                    type="primary"
                    block
                    data-testid="save-button-attribute"
                    onClick={handleSaveWithSkip}
                    disabled={
                      isEdit ? hasErrors() || errorShown : isSaveDisabled
                    }
                    loading={
                      alcoholPredictionLoading ||
                      tobaccoPredictionLoading ||
                      isAutoTranslationLoading
                    }
                  >
                    {getSaveButtonText(
                      showUploadPhoto
                        ? textMap.saveChanges
                        : textMap.itemAttributeButtonCategory,
                    )}
                  </Button>
                ) : (
                  type === "menu" && (
                    <Button
                      type="primary"
                      block
                      data-testid="save-button"
                      onClick={handleSave}
                      disabled={isSaveDisabled}
                    >
                      {isEdit ? textMap.saveChanges : textMap.addItem}
                    </Button>
                  )
                )}
                {mustEditThroughMenu && !enableStockManagement && (
                  <Button data-testid="done-button" block onClick={onClose}>
                    {textMap.done}
                  </Button>
                )}
                {mustEditThroughMenu && enableStockManagement && (
                  <Button
                    type="primary"
                    block
                    data-testid="save-button-stock"
                    disabled={!isTouched}
                    onClick={handleSaveStockManagement}
                  >
                    {textMap.saveChanges}
                  </Button>
                )}
              </Col>
            </Row>
          </div>
        </div>
      </Drawer>
      <PhotoUploadDrawer
        visible={photoDrawerVisible}
        uploadedImg={multiPics?.enable ? multiPicEditingImg : uploadedImg}
        photoUploadTitle={photoUploadTitle}
        onTogglePhotoUploaderVisible={handleTogglePhotoUploaderVisible}
        onSubmitUploadPhoto={
          multiPics?.enable
            ? handleMultiPicSubmitUploadPhoto
            : handleSubmitUploadPhoto
        }
        eventCategory={troyEventCategory}
        {...(!multiPics?.enable
          ? {
              validationHandlingOverride: {
                error: uploadPhotoError,
                warning: uploadPhotoWarning,
                onError: e => setUploadPhotoError(e),
                onWarn: w => setUploadPhotoWarning(w),
              },
            }
          : {})}
      />
      <ItemPreviewDrawer
        previewItem={previewItem}
        showPreview={showPreview}
        modifierGroups={linkedModifierGroups ?? []}
        onClosePreview={handleClosePreview}
      />
      {isServiceBasedMenuEnabled && (
        <AdditionalPriceDrawer
          data-testid="additional-price-drawer"
          visible={showAdditionalPriceDrawer}
          onClose={() => setShowAdditionalPriceDrawer(false)}
          onAdditionalPriceChange={handleChangeAdditionalPrice}
          initialValues={additionalPrice}
          validationType="item"
          existingDefaultPrice={priceValue}
        />
      )}
      {isMenuTranslation && (
        <TranslationDrawer
          data-testid="translation-drawer"
          translationType={TranslationType.item}
          visible={showTranslationDrawer}
          merchantID={merchantID}
          merchantGroupID={merchantGroupID}
          menuGroupID={menuGroupID}
          menuTranslationSetting={menuTranslationSetting}
          nameTranslation={nameTranslationValue}
          resetNameTranslation={item?.resetNameTranslation}
          descriptionTranslation={descriptionTranslationValue}
          resetDescriptionTranslation={item?.resetDescriptionTranslation}
          defaultValue={getFieldValue("itemName")}
          defaultValueDescription={getFieldValue("description")}
          onClose={() => setShowTranslationDrawer(false)}
          onSave={handleOnCloseTranslation}
          disabled={!isAllowEditItem}
          isAutoTranslationEnabled={isAutoTranslationEnabled}
          isEdit={isEdit}
          autoTranslationQueue={autoTranslationQueue}
        />
      )}
    </Fragment>
  )
}
export const ItemDrawer = Form.create<ItemDrawerProps>({})(ItemDrawerComponent)
